<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterGroupSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterGroupSearch"
            v-model="tableGroups.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          type="button"
          variant="primary"
          @click.prevent="createGroup"
        >
          Create Group
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableGroups"
      :provider="tableProvider"
      :fields="tableGroups.fields"
      :filter="tableGroups.filter"
      :filter-on="tableGroups.filterOn"
      :sort-by="tableGroups.sortBy"
      :sort-desc="tableGroups.sortDesc"
      :sort-direction="tableGroups.sortDirection"
      :total-rows="tableGroups.totalRows"
      :current-page="tableGroups.currentPage"
      :per-page="tableGroups.perPage"
      :stacked="tableGroups.stacked"
      :busy="tableGroups.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableGroups.busy"
            @click.prevent="editGroup(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalGroup"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MGroupTitle"
    >
      <ValidationObserver
        ref="formGroup"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="group code"
                vid="group_code"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Code"
                  label-for="group_code"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="group_code"
                    v-model="formGroup.group_code"
                    type="text"
                    maxlength="30"
                    autocomplete="off"
                    placeholder="enter group code here"
                    :state="(errors.length > 0 ? false : null)"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="group name"
                vid="group_name"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Name"
                  label-for="group_name"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="group_name"
                    v-model="formGroup.group_name"
                    type="text"
                    maxlength="100"
                    autocomplete="off"
                    placeholder="enter group name here"
                    :state="(errors.length > 0 ? false : null)"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formGroup.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateGroup"
        >
          {{ state.editing ? 'Update' : 'Create' }} Group
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import { SAdminProductGroup } from '@/services/admin'

export default {
  name: 'VAdminGroups',

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Groups'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formGroup: {
        id: '',
        group_code: '',
        group_name: '',
        active: 1
      },
      tableGroups: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 6, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'group_code', class: 'min-w-175px' },
          { mobile: 3, key: 'group_name', class: 'min-w-175px' },
          { mobile: 4, key: 'active', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 5, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MGroupTitle () {
      return this.state.editing ? 'Edit Group' : 'Create Group'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableGroups.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      }

      return await SAdminProductGroup.get(payload).then(
        ({ data }) => {
          this.tableGroups.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableGroups.totalRows = 0
        return []
      }).finally(() => {
        this.tableGroups.busy = false
      })
    },

    createGroup () {
      this.state.editing = false

      this.formGroup = Object.assign(this.formGroup, {
        id: '',
        group_code: '',
        group_name: '',
        active: 1
      })

      this.$refs.modalGroup.show()
    },

    editGroup (group) {
      this.state.editing = true

      this.formGroup = Object.assign(this.formGroup, {
        id: group.id,
        group_code: group.group_code,
        group_name: group.group_name,
        active: group.active
      })

      this.$refs.modalGroup.show()
    },

    async validateGroup () {
      await this.$refs.formGroup.validate().then(
        async validGroup => {
          if (!validGroup) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Group?</h6>' : '<h6>Create this Group?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putGroup() : this.postGroup()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formGroup.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postGroup () {
      return await SAdminProductGroup.post(this.formGroup).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalGroup.hide()
            this.$refs.tableGroups.refetch()
          })
        }
      )
    },

    async putGroup () {
      return await SAdminProductGroup.put(this.formGroup).then(
        ({ data: { message, product_group: group } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalGroup.hide()
            this.$refs.tableGroups.updateRow({
              id: group.id,
              group_code: group.group_code,
              group_name: group.group_name,
              active: group.active,
              updated_at: group.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
